// @ts-strict-ignore
/**
 * Count menu: clickable list of count types to go inside mp-drop-menu
 */
import isEqual from 'lodash/isEqual';
import * as React from 'react';

import {withStyles} from 'common/util/react';
import {AdvancedMenuValue, CountBy, CountType} from 'common/query-builder/query-entry-section/types';
import {Section, SectionItem} from 'common/components/items-menu/types';
import {ALL_MATH_TYPES, CountUsersOnceState, PropertyType} from 'common/report/constants';
import {
  ChartType,
  COUNT_USERS_ONCE_TYPE,
  MATH_TYPE,
  MetricType,
  PER_USER_AGGREGATIONS,
} from 'common/types/reports/bookmark';
import {DataGroup} from 'common/widgets/data-group-selector/types';
import {ChangeEvent} from 'common/components/select/types';
import {DateRangeAttributes} from 'common/widgets/date-range-picker/models/types';
import {RollingWindowSize} from 'common/util';
import {getSelectedTimePeriodLabel} from 'common/query-builder/query-entry-section/util';
import {ButtonReact} from 'common/components/button';
import {ButtonSize, ButtonTheme} from 'common/components/button/constants';
import {Directions} from 'common/components/drop-menu/types';
import {SelectReact} from 'common/components/select';
import {SelectTheme} from 'common/components/select/constants';
import {SvgIconReact} from 'common/components/svg-icon';
import {Placement} from 'common/components/tooltip/types';
import {IronContextRegistry} from 'common/report/context-registry';
import {useCurrentProject} from 'common/contexts/react/current-project-context';
import {useTranslation} from 'common/i18n/hooks';
import {DEFAULT_REVENUE_CHANGE_BEHAVIOR} from 'common/query-builder/query-entry-section/row-types/behavior/utils';
import {isTimeSeriesChartType} from 'common/report/insights/visualization/charts/chart-display/utils';

import {PropertySectionItem} from '../properties-menu/item-menu-types';
import {IncludeCustomPropertyType, isProperty, SelectedPropertyItem} from '../properties-menu/types';
import {filterToBookmarkablePropertyItem, propertyItemToSegmentation} from '../properties-menu/util';
import {FUNNELS_TIME_TO_CONVERSION} from '../properties-menu/constants';
import {
  AggregationSelector,
  CountTypeSelector,
  FUNNELS_COUNT_MENU_ITEMS,
  INSIGHTS_COUNT_MENU_ITEMS,
  PROFILE_COUNT_MENU_ITEMS,
  RETENTION_COUNT_MENU_ITEMS,
  SubSelectionTypes,
} from './constants';
import {generateSectionItem, getSubSelectionsForCountType, shouldCommitSelection} from './util';
import css from './index.inline.styl';
/*eslint-disable-next-line import/no-cycle*/
import {SubSelections} from './components';

export interface CountMenuState {
  isSubSelectionsVisible: boolean;
  isPropertyMenuOpen: boolean;
  isNestedPropertyMenuOpen: boolean; // cart analysis
  isRevenuePropertyMenuOpen: boolean; // revenue analysis
  isAggregationPerUserMenuOpen: boolean;
  isAggregationMenuOpen: boolean;
  isCountUsersOnceMenuOpen: boolean;
  isAdvancedMenuOpen: boolean;
  // selections
  selectedCountType: CountType;
  selectedCountTypeIndex: number;
  selectedProperty: PropertySectionItem;
  selectedAggregationOperator: MATH_TYPE;
  selectedAggregationOperatorPerUser: PER_USER_AGGREGATIONS;
  selectedCountUsersOnceType: COUNT_USERS_ONCE_TYPE;
  selectedCustomPercentile: number | undefined;
  countTypeSelections: Array<SectionItem<CountTypeSelector>>;
}

export interface CountMenuProps {
  chartType?: ChartType;
  dataGroups?: DataGroup[];
  dateRange?: DateRangeAttributes;
  dateUnit?: RollingWindowSize;
  advancedMenuSections?: Array<Section<AdvancedMenuValue>>;
  countUsersOnceState: CountUsersOnceState;
  otherAggregationOptions?: Array<PropertySectionItem<string>>;
  dataGroupId?: string;
  metricType?: MetricType;
  open?: boolean;
  selected?: CountBy;
  trackName?: string;

  // Flags plumbed for properties-menu
  enableWorkspaceFilterRestrictions?: boolean;
  includeCustomProperties?: IncludeCustomPropertyType;

  // More params plumbed for properties-menu
  // Unclear how much of this is actually needed
  allDataGroups?: boolean;
  hideEditControls?: boolean;
  hideEvents?: boolean;
  projectHasPeople?: boolean;
  showBorrowedProperties?: boolean;
  eventNames?: string[];
  projectId?: number;
  queryOrigin?: `canvas` | `experiment` | `report`;
  readonly?: boolean;
  userId?: number;
  workspaceId?: number;

  // Event handlers
  onSelect: (selection: CountBy) => void;
}

class CountMenu extends React.PureComponent<
  CountMenuProps & Pick<IronContextRegistry, `currentProject` | `i18n`>,
  CountMenuState
> {
  public divRef = React.createRef<HTMLDivElement>();

  state = {
    isSubSelectionsVisible: false,
    isPropertyMenuOpen: false,
    isNestedPropertyMenuOpen: false,
    isRevenuePropertyMenuOpen: false,
    isAggregationPerUserMenuOpen: false,
    isAggregationMenuOpen: false,
    isCountUsersOnceMenuOpen: false,
    isAdvancedMenuOpen: false,
    selectedCountType: null,
    selectedCountTypeIndex: -1,
    selectedProperty: null,
    selectedAggregationOperator: null,
    selectedAggregationOperatorPerUser: null,
    selectedCountUsersOnceType: null,
    selectedCustomPercentile: null,
    countTypeSelections: [],
  };

  componentDidUpdate(prevProps: Readonly<CountMenuProps>): void {
    if (this.props === prevProps) {
      return;
    }
    const changedParams = new Set(Object.keys(this.props).filter((key) => !isEqual(this.props[key], prevProps[key])));

    if (changedParams.has(`dataGroupId`) || changedParams.has(`metricType`)) {
      // getCountTypeSelections relies on getContext, which must not be read before
      // the component has been connected
      this.setState({
        countTypeSelections: this.getCountTypeSelections(),
      });
    }
    if (changedParams.has(`selected`)) {
      this.syncStateFromSelected(this.state.countTypeSelections, this.props.selected);
    }
    if (changedParams.has(`open`)) {
      if (!prevProps.open) {
        // the menu just opened
        const {countType, aggregationOperator, countUsersOnceType} = this.props.selected;
        const canExpand = this.state.countTypeSelections[
          this.state.selectedCountTypeIndex
        ]?.value.canExpandSubSelections({
          countType,
          aggregation: aggregationOperator,
          countUsersOnceType,
        });
        this.setState({
          isSubSelectionsVisible: Boolean(canExpand),
        });
      } else {
        // the menu just closed
        this.handleCloseMenu();
      }
    }
  }

  public componentDidMount() {
    const countTypeSelections = this.getCountTypeSelections();
    this.syncStateFromSelected(countTypeSelections, this.props.selected);
  }

  // `selectedX` state are "staged" state. we need to manually sync them from `selected` attribute
  // this serves similar purpose as `getDerivedStateFromProps`.
  syncStateFromSelected(countTypeSelections: Array<SectionItem<CountTypeSelector>>, selected: CountBy) {
    const {countType, aggregationOperator, aggregationOperatorPerUser, property, countUsersOnceType, percentile} =
      selected;
    const selectedCountTypeIndex = countTypeSelections.findIndex((option) => {
      if (option.value.countType === countType) {
        // check if TTC is selected
        if (property?.value === FUNNELS_TIME_TO_CONVERSION.value) {
          return property.value === option.value?.property?.value;
        }
        return true;
      }
      return false;
    });
    this.setState({
      countTypeSelections,
      selectedCountType: countType,
      selectedCountTypeIndex,
      selectedAggregationOperator: aggregationOperator,
      selectedAggregationOperatorPerUser: aggregationOperatorPerUser,
      selectedProperty: property,
      selectedCountUsersOnceType: countUsersOnceType,
      selectedCustomPercentile: percentile,
    });
  }

  public selectedTimePeriodLabel() {
    const dateRange = this.props.dateRange;
    const chartType = this.props.chartType;
    const dateUnit = this.props.dateUnit;
    return getSelectedTimePeriodLabel(dateRange, chartType, dateUnit);
  }

  private handleCloseMenu() {
    const currentCountTypeSelection = this.state.countTypeSelections[this.state.selectedCountTypeIndex];
    const {countType, canExpandSubSelections: canExpand} = currentCountTypeSelection.value;
    const isSubSelectionsVisible = canExpand({
      countType,
      aggregation: this.state.selectedAggregationOperator,
      countUsersOnceType: this.state.selectedCountUsersOnceType,
    });
    this.setState({
      isSubSelectionsVisible,
    });
    const commit = shouldCommitSelection({
      countTypeSelection: currentCountTypeSelection,
      property: this.state.selectedProperty,
      aggregationOperatorPerUser: this.state.selectedAggregationOperatorPerUser,
      aggregationOperator: this.state.selectedAggregationOperator,
      countUsersOnceType: this.state.selectedCountUsersOnceType,
    });
    // If the current count type does not have required selections, set the count type selection state back to previous selections
    if (!commit) {
      const {
        countType: selectedCountType,
        aggregationOperator: selectedAggregationOperator,
        aggregationOperatorPerUser: selectedAggregationOperatorPerUser,
        property: selectedProperty,
        countUsersOnceType: selectedCountUsersOnceType,
        percentile: selectedCustomPercentile,
      } = this.props.selected;
      this.setState({
        selectedCountType,
        selectedCountTypeIndex: this.state.countTypeSelections.findIndex(
          (option) => option.value.countType === selectedCountType,
        ),
        selectedAggregationOperator,
        selectedAggregationOperatorPerUser,
        selectedProperty,
        selectedCountUsersOnceType,
        selectedCustomPercentile,
      });
    }
  }

  public handleAdvancedMenuChange = (ev: ChangeEvent<AdvancedMenuValue>) => {
    const advancedMenuValue = ev.detail.item.value;
    const countBy: CountBy = {
      countType: this.state.selectedCountType,
      aggregationOperator: this.state.selectedAggregationOperator,
      aggregationOperatorPerUser: this.state.selectedAggregationOperatorPerUser,
      property: this.state.selectedProperty,
      advancedMenuValue,
      percentile: this.state.selectedCustomPercentile,
    };
    this.commitCountBy(countBy);
  };

  public getCountTypeSelections(): Array<SectionItem<CountTypeSelector>> {
    const metricType = this.props.metricType;

    switch (metricType) {
      case MetricType.Event:
        return INSIGHTS_COUNT_MENU_ITEMS.map((item) =>
          generateSectionItem({
            item,
            dataGroupId: this.props.dataGroupId,
            dataGroups: this.props.dataGroups,
          }),
        );
      case MetricType.People:
      case MetricType.Cohort:
        return PROFILE_COUNT_MENU_ITEMS.map((item) =>
          generateSectionItem({
            item,
            dataGroupId: this.props.dataGroupId,
            dataGroups: this.props.dataGroups,
          }),
        );
      case MetricType.Funnel:
        return FUNNELS_COUNT_MENU_ITEMS(true).map((item) =>
          generateSectionItem({
            item,
            dataGroupId: this.props.dataGroupId,
            dataGroups: this.props.dataGroups,
          }),
        );
      case MetricType.Retention:
        return RETENTION_COUNT_MENU_ITEMS(true, true).map((item) =>
          generateSectionItem({
            item,
            dataGroupId: this.props.dataGroupId,
            dataGroups: this.props.dataGroups,
          }),
        );
      case MetricType.RetentionFrequency:
        return RETENTION_COUNT_MENU_ITEMS(false, false).map((item) =>
          generateSectionItem({
            item,
            dataGroupId: this.props.dataGroupId,
            dataGroups: this.props.dataGroups,
          }),
        );
      default:
        return [];
    }
  }

  /**
   * Specifies if the current sub selection can be expanded or not
   * If the previous sub selection has selected value, current sub selection can be expanded
   */
  public canExpandCurrentSubSelection(countTypeIndex: number, previousSelectionType: SubSelectionTypes) {
    if (this.state.selectedCountTypeIndex !== countTypeIndex || !this.state.isSubSelectionsVisible) {
      return false;
    }

    switch (previousSelectionType) {
      case SubSelectionTypes.Property:
        return !!this.state.selectedProperty;
      case SubSelectionTypes.AggregationPerUser:
        // for Aggregation per user, if user property is selected, we do not hide it
        return !!this.state.selectedAggregationOperatorPerUser || !!this.state.selectedProperty;
      case SubSelectionTypes.Aggregation:
        return !!this.state.selectedAggregationOperator;
      default:
        return !!this.state.selectedCountType;
    }
  }

  /** Open sub selection menu */
  public openSubSelectionMenu({
    subSelections,
    subSelectionIndex,
    canExpand = false,
    forceOpen = false,
  }: {
    subSelections: SubSelectionTypes[];
    subSelectionIndex: number;
    canExpand: boolean;
    forceOpen?: boolean;
  }) {
    this.setState({
      isSubSelectionsVisible: forceOpen ? true : canExpand,
    });
    const subSelection = subSelections[subSelectionIndex];
    switch (subSelection) {
      case SubSelectionTypes.Property:
        this.setState({
          isPropertyMenuOpen: true,
        });
        break;
      case SubSelectionTypes.NestedProperty:
        this.setState({
          isNestedPropertyMenuOpen: true,
        });
        break;
      case SubSelectionTypes.AggregationPerUser:
        this.setState({
          isAggregationPerUserMenuOpen: true,
        });
        break;
      case SubSelectionTypes.Aggregation:
        this.setState({
          isAggregationMenuOpen: true,
        });
        break;
      case SubSelectionTypes.CountUsersOnce:
        this.setState({
          isCountUsersOnceMenuOpen: true,
        });
        break;
      case SubSelectionTypes.RevenueProperty:
        this.setState({
          isRevenuePropertyMenuOpen: true,
        });
        break;
    }
  }

  public closeAllSubMenus() {
    this.setState({
      isPropertyMenuOpen: false,
      isAggregationPerUserMenuOpen: false,
      isNestedPropertyMenuOpen: false,
      isAggregationMenuOpen: false,
      isCountUsersOnceMenuOpen: false,
      isAdvancedMenuOpen: false,
      isRevenuePropertyMenuOpen: false,
    });
  }

  public disableTooltips() {
    // disable first level selection (Count Type) tooltips if any of the sub menus are open
    return (
      this.state.isPropertyMenuOpen ||
      this.state.isAggregationPerUserMenuOpen ||
      this.state.isAggregationMenuOpen ||
      this.state.isCountUsersOnceMenuOpen
    );
  }

  /**
   * Handles selecting a caret icon within count option.
   * This will only open the sub selection menu. This will not commit any selections.
   */
  public handleSelectIcon(ev: MouseEvent, selection: SectionItem<CountTypeSelector>, index: number) {
    ev.stopPropagation();

    // De-select existing selections if different options
    if (this.state.selectedCountTypeIndex !== index) {
      this.setState({
        selectedAggregationOperatorPerUser: null,
        selectedAggregationOperator: null,
        selectedProperty: null,
        selectedCustomPercentile: null,
      });
    }

    // Open sub selection menu if exist
    const subSelections = getSubSelectionsForCountType({
      countTypeSelection: selection,
      property: this.state.selectedProperty,
    });
    const {countType, canExpandSubSelections} = selection.value;
    const canExpand = canExpandSubSelections({
      countType,
      aggregation: this.state.selectedAggregationOperator,
      countUsersOnceType: this.state.selectedCountUsersOnceType,
    });
    this.openSubSelectionMenu({
      subSelections,
      subSelectionIndex: 0,
      canExpand,
      forceOpen: true,
    });

    this.setState({
      selectedCountType: selection.value.countType,
      selectedCountTypeIndex: index,
    });
  }

  /**
   * Handles first level selections (Count Types) - i.e. Uniques, Total Events, Sessions
   * @param selection selected count type
   * @param index index of count type within this.state.countTypeSelections
   */
  public handleSelectCountType(selection: SectionItem<CountTypeSelector>, index: number) {
    // De-select existing selections if different options
    if (this.state.selectedCountTypeIndex !== index) {
      this.setState({
        selectedAggregationOperatorPerUser: null,
        selectedAggregationOperator: null,
        selectedProperty: null,
        selectedCustomPercentile: null,
      });
    }

    const {countType, operator, property, canExpandSubSelections} = selection.value;
    this.setState({
      selectedCountType: countType,
      selectedCountTypeIndex: index,
      selectedProperty: property,
      ...(operator && {
        selectedAggregationOperator: operator,
      }),
    });

    // Open sub selection menu if exist
    const subSelections = getSubSelectionsForCountType({
      countTypeSelection: selection,
      property,
    });
    const canExpand = canExpandSubSelections({
      countType,
      aggregation: operator,
      countUsersOnceType: this.state.selectedCountUsersOnceType,
    });
    this.openSubSelectionMenu({
      subSelections,
      subSelectionIndex: 0,
      canExpand,
    });

    const commit = shouldCommitSelection({
      countTypeSelection: selection,
      ...(operator && {
        aggregationOperator: operator,
      }),
    });
    if (commit) {
      const countBy: CountBy = {
        countType,
        aggregationOperator: operator,
        advancedMenuValue: this.props.selected.advancedMenuValue,
        property,
        percentile: this.state.selectedCustomPercentile,
      };
      this.commitCountBy(countBy);
    }
  }

  /**
   * Handles second level selection (Property) in Sub-selections
   * @param ev Select event from PropertiesMenu
   * @param subSelectionIndex index of SubSelections
   */
  public handleSelectProperty(
    selection: SelectedPropertyItem,
    subSelectionIndex: number,
    {
      isNestedProperty,
      isRevenueProperty,
    }: {
      isNestedProperty: boolean;
      isRevenueProperty: boolean;
    },
  ) {
    const {rawItem: property} = selection;
    const countTypeSelection = this.state.countTypeSelections[this.state.selectedCountTypeIndex];

    let updatedProperty: PropertySectionItem = property;

    if (isNestedProperty && isProperty(property)) {
      updatedProperty = {
        ...this.state.selectedProperty,
        joinPropertyType: PropertyType.List,
        propertyDefaultType: PropertyType.Object,
        listItemGroup: propertyItemToSegmentation(property, property.resourceType),
      };
    }

    if (isRevenueProperty && isProperty(property)) {
      updatedProperty = {
        ...this.state.selectedProperty,
        behavior: {
          property: filterToBookmarkablePropertyItem(property),
          offset: {
            unit: this.props.dateUnit,
            value: isTimeSeriesChartType(this.props.chartType) ? -1 : `$start`,
          },
          // TODO: enrich default revenue change behavior and spread this object
          behaviorType: DEFAULT_REVENUE_CHANGE_BEHAVIOR.behaviorType,
          changeType: `any`,
        },
      };
    }

    this.setState({
      selectedProperty: updatedProperty,
    });

    // Open next sub selection menu if exist
    const subSelections = getSubSelectionsForCountType({
      countTypeSelection,
      property: updatedProperty,
    });
    const canExpand = countTypeSelection.value.canExpandSubSelections({
      countType: this.state.selectedCountType,
      aggregation: this.state.selectedAggregationOperator,
    });
    this.openSubSelectionMenu({
      subSelections,
      subSelectionIndex: subSelectionIndex + 1,
      canExpand,
    });

    const commit = shouldCommitSelection({
      countTypeSelection,
      property: updatedProperty,
      aggregationOperatorPerUser: this.state.selectedAggregationOperatorPerUser,
      aggregationOperator: this.state.selectedAggregationOperator,
    });
    if (commit) {
      const countBy: CountBy = {
        countType: this.state.selectedCountType,
        aggregationOperator: this.state.selectedAggregationOperator,
        aggregationOperatorPerUser: this.state.selectedAggregationOperatorPerUser,
        property: updatedProperty,
        percentile: this.state.selectedCustomPercentile,
      };
      this.commitCountBy(countBy);
    }
  }

  /**
   * Handles second level selections (AggregationPerUser, Aggregation) in Sub-selections
   * @param aggregation selected aggregation section item from Aggregation Selection
   * @param subSelectionIndex index of SubSelections
   */
  public async handleSelectAggregation(aggregation: SectionItem<AggregationSelector>, subSelectionIndex: number) {
    if (aggregation.value.requiredModalTrigger) {
      try {
        const result = await aggregation.value.requiredModalTrigger({
          aggregation,
          subSelectionIndex,
        });

        if (result) {
          this.processAggregationSelectionInternal(result.aggregation, result.subSelectionIndex);
        }
      } catch (_error) {
        // User cancelled or error occurred, do nothing
      }
      return;
    }

    this.processAggregationSelectionInternal(aggregation, subSelectionIndex);
  }

  private processAggregationSelectionInternal(
    aggregation: SectionItem<AggregationSelector>,
    subSelectionIndex: number,
  ) {
    const {operator, perUserOperator, countUsersOnceType, percentile} = aggregation.value;
    const countTypeSelection = this.state.countTypeSelections[this.state.selectedCountTypeIndex];
    const hasSelectedCountUsersOnceType = Object.values(COUNT_USERS_ONCE_TYPE).some(
      (type) => type === countUsersOnceType,
    );
    const nextCountUsersOnceType = hasSelectedCountUsersOnceType
      ? countUsersOnceType
      : this.state.selectedCountUsersOnceType;
    const nextSelectedAggregationOperator =
      operator ||
      this.state.selectedAggregationOperator ||
      (hasSelectedCountUsersOnceType ? ALL_MATH_TYPES.UNIQUE : null);
    // in the case of per user aggregation, the aggregation data came from the additional modal in the last step
    // so we need to use the data from the state already set.
    const nextPercentile = percentile ?? (perUserOperator ? this.state.selectedCustomPercentile : undefined);
    this.setState({
      selectedAggregationOperatorPerUser: perUserOperator,
      selectedAggregationOperator: nextSelectedAggregationOperator,
      selectedCountUsersOnceType: nextCountUsersOnceType,
      selectedCustomPercentile: nextPercentile,
    });

    // Open next sub selection menu if exist
    const subSelections = getSubSelectionsForCountType({
      countTypeSelection,
      property: this.state.selectedProperty,
    });
    const canExpand = countTypeSelection.value.canExpandSubSelections({
      countType: this.state.selectedCountType,
      aggregation: nextSelectedAggregationOperator,
      countUsersOnceType: nextCountUsersOnceType,
    });
    this.openSubSelectionMenu({
      subSelections,
      subSelectionIndex: subSelectionIndex + 1,
      canExpand,
    });

    const commit = shouldCommitSelection({
      countTypeSelection,
      property: this.state.selectedProperty,
      aggregationOperatorPerUser: perUserOperator,
      aggregationOperator: nextSelectedAggregationOperator,
      countUsersOnceType: nextCountUsersOnceType,
    });
    if (commit) {
      const countBy: CountBy = {
        countType: this.state.selectedCountType,
        aggregationOperator: nextSelectedAggregationOperator,
        aggregationOperatorPerUser: perUserOperator,
        property: this.state.selectedProperty,
        countUsersOnceType: nextCountUsersOnceType,
        advancedMenuValue: this.props.selected.advancedMenuValue,
        percentile: nextPercentile,
      };
      this.commitCountBy(countBy);
    }
  }

  public commitCountBy(countBy: CountBy) {
    this.closeAllSubMenus();
    this.props.onSelect(countBy);
  }

  render() {
    const {t, print} = this.props.i18n;

    return (
      <div ref={this.divRef} className='mp-count-menu'>
        {this.props.open && (
          <>
            <div className='count-header'>
              <div className='count-title'>{t(`query-builder.count-menu.measuring`)}</div>
              {this.props.advancedMenuSections.length > 0 && (
                <div className='count-advance-options'>
                  <SelectReact
                    open={this.state.isAdvancedMenuOpen}
                    alwaysShowCaret
                    label={t(`query-builder.advanced`)}
                    theme={SelectTheme.Tertiary}
                    size={ButtonSize.Small}
                    menuAlignment={Directions.Right}
                    menuWidth={184}
                    disableFirstItemPadding
                    elref={`advancedCountMenuSelect`}
                    sections={this.props.advancedMenuSections}
                    onChange={this.handleAdvancedMenuChange}
                  />
                </div>
              )}
            </div>
            <div className='count-option-list'>
              {this.state.countTypeSelections.map((countTypeSelection, index) => {
                const subSelections = getSubSelectionsForCountType({
                  countTypeSelection,
                  property: this.state.selectedProperty,
                });
                const isSelected = this.state.selectedCountTypeIndex === index;
                return (
                  <div key={`${index}-${countTypeSelection}`} className='count-option-wrapper'>
                    <ButtonReact
                      className='count-option truncated'
                      theme={isSelected ? ButtonTheme.Primary : ButtonTheme.QueryBuilder}
                      notFocusable
                      tooltip={
                        countTypeSelection.tooltip && !this.disableTooltips()
                          ? {
                              placement: Placement.Right,
                              ...countTypeSelection.tooltip,
                            }
                          : null
                      }
                      onClick={() => this.handleSelectCountType(countTypeSelection, index)}
                    >
                      <div className='count-option-label-wrapper'>
                        <div className='option-label' elref={`countOptionLabel`}>
                          {print(countTypeSelection.label)}
                        </div>
                        {countTypeSelection.value.expanderIcon ? (
                          <ButtonReact
                            className='option-icon-button'
                            icon={countTypeSelection.value.expanderIcon}
                            size={ButtonSize.Small}
                            theme={isSelected ? ButtonTheme.Primary : ButtonTheme.Tertiary}
                            onClick={(ev) => this.handleSelectIcon(ev, countTypeSelection, index)}
                          />
                        ) : subSelections.length > 0 ? (
                          <SvgIconReact className='option-icon' icon={isSelected ? `caret-up` : `caret-down`} />
                        ) : null}
                      </div>
                    </ButtonReact>
                    <SubSelections
                      selections={subSelections}
                      countTypeIndex={index}
                      countMenuProps={this.props}
                      countMenuState={this.state}
                      timePeriodLabel={this.selectedTimePeriodLabel()}
                      disableTooltips={this.disableTooltips()}
                      canExpandCurrentSubSelection={this.canExpandCurrentSubSelection.bind(this)}
                      onSelectProperty={({selection, selectionIndex, isNestedProperty, isRevenueProperty}) => {
                        this.handleSelectProperty(selection, selectionIndex, {
                          isNestedProperty,
                          isRevenueProperty,
                        });
                      }}
                      onSelectAggregation={this.handleSelectAggregation.bind(this)}
                      onMenuChange={(menuState: Pick<CountMenuState, keyof CountMenuState>) => {
                        this.setState(menuState);
                      }}
                      contextPanelParent={this.divRef.current}
                    />
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    );
  }
}

export const CountMenuReact = withStyles(
  React.forwardRef<CountMenu, CountMenuProps>((props, ref) => {
    const currentProject = useCurrentProject();
    const i18n = useTranslation();

    return <CountMenu ref={ref} currentProject={currentProject} i18n={i18n} {...props} />;
  }),
  css,
);
